/**
 * What this deployment can actually do, asked once by the agent editor's
 * Connectors tab.
 *
 * The tab previously described the Drive capability in the conditional —
 * "…when in-place editing is enabled on the platform". A person reading their
 * own agent's settings cannot resolve that clause, so the switch told them
 * nothing about what they were switching. The fact is only knowable
 * server-side, so the editor asks, and then states one thing.
 */

import { useEffect, useState } from 'react';
import api from '@/services/api';

export interface AgentAuthoringCapabilities {
  /** The organisation's ceiling for rewriting existing Drive files
      (super-admin flag `integrations.cloud_storage.editing`) — the only
      switch above an agent's own "Edit files in Drive". */
  driveTenantEditing: boolean;
  /** Whether this user has linked a Google account at all. */
  driveConnected: boolean;
  loaded: boolean;
}

const EMPTY: AgentAuthoringCapabilities = {
  driveTenantEditing: false,
  driveConnected: false,
  loaded: false,
};

export function useAgentAuthoringCapabilities(enabled = true): AgentAuthoringCapabilities {
  const [caps, setCaps] = useState<AgentAuthoringCapabilities>(EMPTY);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    // Optional: a failure leaves the editor in its cautious default (no
    // in-place editing), which understates rather than overstates what the
    // agent can do.
    api.get('/cloud-storage/capabilities').catch(() => null).then((driveRes) => {
      if (cancelled) return;
      const drive = driveRes?.data?.google_drive || {};
      setCaps({
        driveTenantEditing: drive.tenant_editing === true,
        driveConnected: drive.connected === true,
        loaded: true,
      });
    });

    return () => { cancelled = true; };
  }, [enabled]);

  return caps;
}
